import {
  NOISE_RULESETS,
  RULES_BUNDLE_VERSION,
  resolveRuleset,
  type BarrierType,
  type Jurisdiction,
  type LineOfSight,
} from "@sts/rules-data";
import { ComplianceError, ENGINE_VERSION } from "./heat-loss.js";
import type { CalcStamp, CalcWarning } from "./types.js";

export interface HeatPumpSoundSource {
  readonly id: string;
  /**
   * A-weighted sound power level, dB(A), from the manufacturer datasheet.
   * MCS 020 a) Step 3: this must be sound POWER, not sound pressure, and the
   * "low noise mode" figure must not be used.
   */
  readonly soundPowerLevelDb: number;
  /**
   * Directivity factor Q from the number of reflecting surfaces within 1 m:
   * 2 = one surface, 4 = two, 8 = three. More than three fails MCS 020 a).
   */
  readonly directivityFactor: number;
  /** Straight-line distance to the assessment position, m (measured to 0.1 m). */
  readonly distanceM: number;
  readonly barrierType: BarrierType;
  readonly lineOfSight: LineOfSight;
}

export interface AssessmentPosition {
  readonly id: string;
  /**
   * MCS 020 a): 1 m external to the centre point of any door or window to a
   * habitable room of a neighbouring property, perpendicular to its plane.
   */
  readonly description: string;
  readonly sources: readonly HeatPumpSoundSource[];
}

export interface SourceContribution {
  readonly sourceId: string;
  readonly barrierAttenuationDb: number;
  readonly soundPressureLevelDb: number;
}

export interface PositionAssessment {
  readonly positionId: string;
  readonly description: string;
  readonly contributions: readonly SourceContribution[];
  /** Combined sound pressure level at this position, dB(A), to 0.01. */
  readonly totalSoundPressureLevelDb: number;
  /**
   * The same level rounded as MCS 020 a) Step 8 requires ("to the nearest 0.1
   * decimal place") — the figure that is compared with the limit and reported.
   */
  readonly reportedSoundPressureLevelDb: number;
  readonly limitDb: number;
  /** Whether the level alone is at or below the limit, ignoring other breaches. */
  readonly withinLimit: boolean;
  /** Whether this position complies with the standard overall. */
  readonly passes: boolean;
}

export interface NoiseAssessmentResult {
  readonly positions: readonly PositionAssessment[];
  /** Overall pass requires every assessed position to pass. */
  readonly passes: boolean;
  readonly limitDb: number;
  readonly metric: string;
  readonly warnings: readonly CalcWarning[];
  readonly stamp: CalcStamp;
}

export interface NoiseAssessmentInput {
  readonly jurisdiction: Jurisdiction;
  readonly calculationDate: string;
  readonly positions: readonly AssessmentPosition[];
  readonly complianceMode: boolean;
}

function round(n: number, dp = 2): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/**
 * MCS 020 a) Step 7 — sound pressure level at an assessment position:
 *
 *   Lp = Lw + 10·log10( Q / (4·π·r²) ) − A_B
 */
export function soundPressureLevel(
  soundPowerLevelDb: number,
  directivityFactor: number,
  distanceM: number,
  barrierAttenuationDb: number,
): number {
  if (distanceM <= 0) {
    throw new Error("Distance to the assessment position must be greater than zero.");
  }
  return (
    soundPowerLevelDb +
    10 * Math.log10(directivityFactor / (4 * Math.PI * distanceM ** 2)) -
    barrierAttenuationDb
  );
}

/**
 * MCS 020 a) — logarithmic summation of multiple heat pumps sharing an
 * assessment position: Lp,total = 10·log10( Σ 10^(Lp,i / 10) ).
 *
 * Issue 1.1, Step 7 (p.14): "Where two heat pumps are installed and have the
 * same assessment position(s), the sound pressure level at the assessment
 * position for each heat pump shall be calculated and then the total sound
 * pressure level shall be calculated using the logarithmic summation", which
 * "can be expanded for more than two heat pumps"; Table 2 Step 7b (p.10).
 * Step 8 then rounds the TOTAL to 0.1 — so pass unrounded levels in.
 */
export function combineSoundPressureLevels(levelsDb: readonly number[]): number {
  if (levelsDb.length === 0) return -Infinity;
  const sum = levelsDb.reduce((acc, l) => acc + 10 ** (l / 10), 0);
  return 10 * Math.log10(sum);
}

export function assessNoise(input: NoiseAssessmentInput): NoiseAssessmentResult {
  const ruleset = resolveRuleset(NOISE_RULESETS, {
    on: input.calculationDate,
    jurisdiction: input.jurisdiction,
  });
  const rules = ruleset.data;
  const warnings: CalcWarning[] = [];

  // Architecture decision: compliance-grade results never come from provisional data.
  if (ruleset.provenance === "provisional") {
    warnings.push({
      code: "PROVISIONAL_RULESET",
      severity: "compliance-failure",
      message:
        `The noise ruleset in force for ${input.jurisdiction} (${ruleset.source.standard} ` +
        `issue ${ruleset.source.issue}) is provisional and its assessment method is not yet ` +
        `implemented. A compliance-grade assessment cannot be produced.`,
    });
  }

  if (input.positions.length === 0) {
    warnings.push({
      code: "NO_ASSESSMENT_POSITIONS",
      severity: "compliance-failure",
      message:
        "No assessment positions were provided. MCS 020 a) requires every potentially affected " +
        "position to be assessed — the nearest is not always the worst case.",
    });
  }

  const positions: PositionAssessment[] = input.positions.map((pos) => {
    // A source whose directivity factor is outside the permitted set fails the
    // standard regardless of how quiet the arithmetic comes out. Tracked here so
    // the position's verdict can account for it.
    let hasInvalidDirectivity = false;
    // Each source's level unrounded, so the Step 8 rounding below is applied
    // once, to the true total — never to a figure already rounded to 0.01
    // (31.049 would otherwise become 31.05 and then 31.1, not the 31.0 the
    // standard's own worked example reports).
    const rawLevels: number[] = [];

    const contributions: SourceContribution[] = pos.sources.map((src) => {
      if (!rules.permittedDirectivityFactors.includes(src.directivityFactor)) {
        hasInvalidDirectivity = true;
        warnings.push({
          code: "MCS020A_INVALID_DIRECTIVITY",
          severity: "compliance-failure",
          subjectId: src.id,
          message:
            `Directivity factor Q=${src.directivityFactor} is not permitted. MCS 020 a) allows ` +
            `Q of ${rules.permittedDirectivityFactors.join(", ")} — a heat pump with more than three ` +
            `reflective surfaces (e.g. in a small lightwell) will not meet MCS 020 a).`,
        });
      }

      const barrierAttenuationDb =
        rules.barrierAttenuationDb[src.barrierType][src.lineOfSight];

      // Distance is measured to the nearest 0.1 m (Step 5).
      const roundingM = rules.distanceRoundingM;
      const distanceM = Math.round(src.distanceM / roundingM) * roundingM;

      const raw = soundPressureLevel(
        src.soundPowerLevelDb,
        src.directivityFactor,
        distanceM,
        barrierAttenuationDb,
      );
      rawLevels.push(raw);
      return {
        sourceId: src.id,
        barrierAttenuationDb,
        soundPressureLevelDb: round(raw),
      };
    });

    const rawTotal = combineSoundPressureLevels(rawLevels);
    const totalSoundPressureLevelDb = round(rawTotal);

    // Step 8: round to the nearest 0.1 dB, THEN compare with the limit — so
    // 37.04 dB(A) reports as 37.0 and complies, exactly as the standard reads.
    const step = rules.resultRoundingDb;
    const reportedSoundPressureLevelDb = round(Math.round(rawTotal / step) * step, 1);
    const withinLimit = reportedSoundPressureLevelDb <= rules.limitDb;

    // "passes" has to mean compliant, not merely quiet. A heat pump in a
    // lightwell with four reflecting surfaces can compute to a low level while
    // breaching MCS 020 a) outright, and a caller reading this field would
    // otherwise certify an installation the standard rejects.
    const passes = withinLimit && !hasInvalidDirectivity;

    if (!withinLimit) {
      warnings.push({
        code: "MCS020A_LIMIT_EXCEEDED",
        severity: "compliance-failure",
        subjectId: pos.id,
        message:
          `Assessment position "${pos.description}" reaches ${reportedSoundPressureLevelDb.toFixed(1)} dB(A), ` +
          `above the ${rules.limitDb} ${rules.metric} permitted development limit. Consider ` +
          `relocation, a compliant barrier, or a quieter unit.`,
      });
    }

    return {
      positionId: pos.id,
      description: pos.description,
      contributions,
      totalSoundPressureLevelDb,
      reportedSoundPressureLevelDb,
      limitDb: rules.limitDb,
      withinLimit,
      passes,
    };
  });

  if (input.complianceMode) {
    const failures = warnings.filter((w) => w.severity === "compliance-failure");
    if (failures.length > 0) {
      throw new ComplianceError("Noise assessment failed in compliance mode.", failures);
    }
  }

  return {
    positions,
    passes: positions.length > 0 && positions.every((p) => p.passes),
    limitDb: rules.limitDb,
    metric: rules.metric,
    warnings,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: input.calculationDate,
      complianceMode: input.complianceMode,
    },
  };
}
